import { Options, Vue } from 'vue-class-component';
import { AuthorizedUser } from '@/trust-admin-app/models/authorized-user';
import store from '@/trust-admin-app/store';
import { Inject } from 'vue-property-decorator';
import { TrustAPI } from '@/shared/modules/trust-api';
import LoadingSpinner from '@/shared/components/loading-spinner/index.vue';
import { RouteN } from '@/trust-admin-app/constants/router-name';
import { AppInsights } from '@/trust-admin-app/appInsights';
import { EventEmitter } from 'events';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import {
  PracticeAccountDetailsRequest,
  PracticeAdditionalContactRequest,
  PracticeStatusRequest,
  PracticeView,
  Priority,
  TrustAccountStatus,
} from '@/shared/modules/typescript-trustapi-client/models';
import AppointEeModal from './appoint-ee-modal/index.vue';
import ConfirmDialogue from '@/shared/components/confirm-dialogue/index.vue';

declare function openNewBrowserUrl(url: string): boolean;

@Options({
  components: {
    LoadingSpinner,
    LoadingIndicator,
    AppointEeModal,
    ConfirmDialogue,
  },
  mixins: [FormatDateTimeMixin],
})
export default class PracticeMain extends Vue {
  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Inject({ from: 'priorities', default: [] })
  private priorities!: Priority[];

  @Inject({ from: 'trustAccountStatus', default: [] })
  private trustAccountStatus!: TrustAccountStatus[];

  // private validatingEntrance = false;
  private ai = new AppInsights();

  private hidePracticeInfo = false;
  private hideLeftPanel = false;

  private editingPracticeStatus = false;
  private savingPracticeStatus = false;
  private editingAccountingDetails = false;
  private savingAccountingDetails = false;

  private loadingEE = false;
  private selectedEE = {};
  private showAppointEE = false;

  private selectedAdditionalContact: PracticeAdditionalContactRequest = {
    id: null as any,
    practiceId: null as any,
    name1: null as any,
    phoneNumber1: null as any,
    email1: null as any,
    name2: null as any,
    phoneNumber2: null as any,
    email2: null as any,
  };

  private loadingAdditionalContacts = false;
  private editingAdditionalContacts = false;
  private loadingContent = false;

  private practiceStatusForm: PracticeStatusRequest = {
    priorityId: null as any,
    trustAccountStatusId: null as any,
  };

  private practiceAccountingDetailsForm: PracticeAccountDetailsRequest = {
    bookkeeper: null as any,
    inspectResultsDiscussedWith: null as any,
    reInspectionPeriodInMonths: null as any,
  };

  private canSaveAccountingDetails = false;

  created(): void {
    this.validateEntrance();
  }

  mounted(): void {
    window.scrollTo(0, 0);
  }

  private async validateEntrance(): Promise<void> {
    this.loadingContent = true;
    const practiceId: number = parseInt(this.$route.query.pId as string, 10);

    try {
      if (!practiceId) {
        throw new Error('No Practice selected');
      } else if (this.selectedPractice.id === practiceId) {
        this.loadingContent = false;
      } else if (this.selectedPractice.id !== practiceId) {
        const resp: any = await this.apiService.PracticeApi?.apiV2PracticeGetPracticeByIdPracticeIdGet(practiceId);
        const practice: PracticeView = resp.data.data;

        if (practice) {
          store.dispatch('setSelectedPractice', practice as PracticeView);
          this.loadingContent = false;
        } else {
          throw new Error('Invalid Practice');
        }
      }
      if (this.selectedPractice) {
        this.practiceStatusForm = {
          id: this.selectedPractice.id,
          priorityId: this.selectedPractice?.priority?.id ?? (null as any),
          trustAccountStatusId: this.selectedPractice?.trustAccountStatus?.id ?? (null as any),
        };

        this.practiceAccountingDetailsForm = {
          id: this.selectedPractice.id,
          bookkeeper: this.selectedPractice.bookkeeper,
          inspectResultsDiscussedWith: this.selectedPractice.inspectResultsDiscussedWith,
          reInspectionPeriodInMonths: this.selectedPractice.reInspectionPeriodInMonths,
        };

        this.loadEE(practiceId);
        this.loadAdditionalContact(practiceId);
      }
    } catch (err: any) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Invalid Practice');
      this.ai.trackException('Error loading page: ' + 'Invalid Practice');
      this.$router.push({ name: RouteN.TA.Home.n });
      console.log(err);
    }
  }

  private async loadEE(practiceId: number): Promise<void> {
    this.loadingEE = true;
    await this.apiService.PracticeAtcsApi?.apiV2AtcsPracticeAdminGetExternalExaminerByPracticeIdPracticeIdGet(
      practiceId,
    )
      .then((resp: any) => {
        this.selectedEE = resp.data.data;
        this.loadingEE = false;
      })
      .catch((err: any) => {
        this.loadingEE = false;
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to load External Examiner record');
        this.ai.trackException('Unable to load External Examiner record: ' + err);
      });
  }

  private async loadAdditionalContact(practiceId: number): Promise<void> {
    this.loadingAdditionalContacts = true;
    await this.apiService.PracticeApi?.apiV2PracticeGetPracticeAdditionalContactPracticeIdGet(practiceId)
      .then((resp: any) => {
        if (resp.data) {
          this.selectedAdditionalContact.id = resp.data.data?.id || 0;
          this.selectedAdditionalContact.practiceId = resp.data.data?.practiceId || practiceId;
          this.selectedAdditionalContact.name1 = resp.data.data?.name1 || null;
          this.selectedAdditionalContact.phoneNumber1 = resp.data.data?.phoneNumber1 || null;
          this.selectedAdditionalContact.email1 = resp.data.data?.email1 || null;
          this.selectedAdditionalContact.name2 = resp.data.data?.name2 || null;
          this.selectedAdditionalContact.phoneNumber2 = resp.data.data?.phoneNumber2 || null;
          this.selectedAdditionalContact.email2 = resp.data.data?.email2 || null;
        }
        this.loadingAdditionalContacts = false;
      })
      .catch((err: any) => {
        this.loadingAdditionalContacts = false;
        console.log(err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to load Additional Contact');
        this.ai.trackException('Unable to load Additional Contact: ' + err);
      });
  }

  private async savePracticeDetails(): Promise<void> {
    try {
      this.editingPracticeStatus = true;
      this.savingPracticeStatus = true;
      const resp: any = await this.apiService.PracticeApi?.apiV2PracticeEditPracticeStatusPost(this.practiceStatusForm);

      const resp1: any = await this.apiService.PracticeApi?.apiV2PracticeGetPracticeByIdPracticeIdGet(
        this.selectedPractice.id,
      );

      store.dispatch('setSelectedPractice', resp1.data.data as PracticeView);
    } catch (err) {
      console.log(err);
    } finally {
      this.savingPracticeStatus = false;
      this.editingPracticeStatus = false;
    }
  }

  private async saveAccountingDetails(): Promise<void> {
    try {
      this.editingAccountingDetails = true;
      this.savingAccountingDetails = true;
      const resp: any = await this.apiService.PracticeApi?.apiV2PracticeEditPracticeAccountingDetailsPost(
        this.practiceAccountingDetailsForm,
      );

      const resp1: any = await this.apiService.PracticeApi?.apiV2PracticeGetPracticeByIdPracticeIdGet(
        this.selectedPractice.id,
      );

      store.dispatch('setSelectedPractice', resp1.data.data as PracticeView);
    } catch (err) {
      console.log(err);
    } finally {
      this.savingAccountingDetails = false;
      this.editingAccountingDetails = false;
    }
  }

  private cancelEditAccountingDetails() {
    this.practiceAccountingDetailsForm = {
      id: this.selectedPractice.id,
      bookkeeper: this.selectedPractice.bookkeeper,
      inspectResultsDiscussedWith: this.selectedPractice.inspectResultsDiscussedWith,
      reInspectionPeriodInMonths: this.selectedPractice.reInspectionPeriodInMonths,
    };
    this.savingAccountingDetails = false;
    this.editingAccountingDetails = false;
  }

  get usrAcct(): AuthorizedUser {
    return store.getters.getUserAccount;
  }

  get selectedPractice(): PracticeView {
    return store.getters.getSelectedPractice;
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private checkAccountingDetails() {
    const chk1: boolean =
      this.practiceAccountingDetailsForm.bookkeeper !== null &&
      this.practiceAccountingDetailsForm.bookkeeper.trim().length > 0;
    const chk2: boolean =
      this.practiceAccountingDetailsForm.inspectResultsDiscussedWith !== null &&
      this.practiceAccountingDetailsForm.inspectResultsDiscussedWith.trim().length > 0;
    const chk3: boolean = this.practiceAccountingDetailsForm.reInspectionPeriodInMonths !== null;

    this.canSaveAccountingDetails = chk1 && chk2 && chk3;
  }

  private showHideOptions(hideLeftPanel: boolean) {
    this.hideLeftPanel = !hideLeftPanel;
    if (this.hideLeftPanel) {
      this.hidePracticeInfo = true;
    } else {
      setTimeout(() => {
        this.hidePracticeInfo = false;
      }, 500);
    }
    this.eventCtrl.emit('side-panel-hidden', this.hideLeftPanel);
  }

  private openExternalURL(url: string) {
    url = url.indexOf('https://') == 0 ? url : 'https://' + url;
    if (typeof openNewBrowserUrl === 'function') {
      openNewBrowserUrl(url);
    } else {
      window.open(url, '_blank');
    }
  }

  private async addEditingEE() {
    this.showAppointEE = true;
  }

  private async editAdditionalContact() {
    this.editingAdditionalContacts = true;
  }

  private async saveAdditionalContacts() {
    this.loadingAdditionalContacts = true;
    this.apiService.PracticeApi?.apiV2PracticeSavePracticeAdditionalContactPost(this.selectedAdditionalContact)
      .then(() => {
        this.loadingAdditionalContacts = false;
        this.editingAdditionalContacts = false;
        this.eventCtrl.emit('show-snackbar', 'success', 'Additional Contact saved');
      })
      .catch((err: any) => {
        this.loadingAdditionalContacts = false;
        this.editingAdditionalContacts = false;
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to save Additional Contact');
        this.ai.trackException('Unable to save Additional Contact: ' + err);
      });
  }

  private async cancelEditAdditionalContacts() {
    this.editingAdditionalContacts = false;
    this.loadAdditionalContact(this.selectedPractice.id);
  }

  private deleteEE(i: any, ee: any): void {
    const nameString = ee.firstName && ee.lastName ? `${ee.firstName} ${ee.lastName}` : '-';
    const _confirm: any = this.$refs.confirmTerminateDialogue;
    _confirm
      .show({
        title: 'Terminate External Examiner',
        message: 'Are you sure you want to terminate <b>' + nameString + "</b> as this Practice's External Examiner?",
        okButton: 'Confirm',
        cancelButton: 'Cancel',
      })
      .then((res: any) => {
        if (res) {
          this.apiService.PracticeAtcsApi?.apiV2AtcsPracticeAdminTerminateExternalExaminerPost({
            externalExaminerId: ee.id,
            practiceId: i.id,
          })
            .then(() => {
              this.eventCtrl.emit('show-snackbar', 'success', 'External Examiner terminated');
              this.loadEE(i.id);
            })
            .catch((err: any) => {
              if (err.response?.data?.errorCode == 90002) {
                this.eventCtrl.emit('show-snackbar', 'error', 'There is a pending request for the External Examiner. Go to Maintain Trust Data > External Examiner Approval and do the appropriate action.');
              } else {
                this.eventCtrl.emit('show-snackbar', 'error', 'Unable to terminate External Examiner');
              }
              this.ai.trackException('Unable to terminate External Examiner: ' + err);
            });
        }
      });
  }
}
